import React, { FC } from 'react';
import { View } from 'react-native';

import { ActivityEntryBody } from '../Activity/ActivityEntryBody';
import { Body1Bold, Caption1Bold, Caption1Reg } from '../../config/designSystem/typography';
import { COLORS } from '../../config/designSystem/Colors';
import { SPACING } from '../../config/designSystem/Spacing';
import { TouchableText } from '../TouchableText/TouchableText';
import { convertFromUtf16 } from '../../utils/others/emoji';

/**
 * figma: the Calendar tab of the Activity Page (node 9693-21682):
 * "Week of <date>" + the challenge days on a weekday strip, the entries
 * written so far below (newest first) and a "Complete today ✏️" action while
 * today is still open.
 */
export interface ChallengeDay {
  done: boolean;
  maxDate?: string | Date | null;
  dateOfCompletion?: string | Date | null;
  entry?: string | null;
}

interface ChallengeCalendarProps {
  days: ChallengeDay[];
  onPressCompleteToday?: () => void;
  /** memo decks are identical every day: their calendar hides the entry card */
  showEntry?: boolean;
  labels?: {
    WEEK_OF?: string;
    COMPLETE_TODAY?: string;
    DAY?: string;
    NO_ENTRY_YET?: string;
  };
}

const DAY_SIZE = 36;

/** the date each challenge day is meant to be done (maxDate is the deadline) */
const scheduledDate = (day: ChallengeDay): Date | null => {
  if (day.dateOfCompletion) {
    return new Date(day.dateOfCompletion);
  }
  if (!day.maxDate) {
    return null;
  }
  const date = new Date(day.maxDate);
  date.setDate(date.getDate() - 1);
  return date;
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const ChallengeCalendar: FC<ChallengeCalendarProps> = ({
  days,
  onPressCompleteToday,
  showEntry = true,
  labels,
}) => {
  const today = new Date();

  // done days sit on their completion date; open days are re-anchored on
  // today (maxDate reflects the original schedule, which drifts into the
  // past as soon as one day is missed)
  const firstOpenIndex = days.findIndex(day => !day.done);
  // when today's day is already done, the next open day is tomorrow — not
  // today again (that put two days on the same weekday and shifted the rest)
  const doneToday = days.some(day => day.done && !!day.dateOfCompletion && isSameDay(new Date(day.dateOfCompletion), today));
  const displayDate = (day: ChallengeDay, index: number): Date | null => {
    if (day.done && day.dateOfCompletion) {
      return new Date(day.dateOfCompletion);
    }
    if (firstOpenIndex !== -1 && index >= firstOpenIndex) {
      const date = new Date(today);
      date.setDate(date.getDate() + (index - firstOpenIndex) + (doneToday ? 1 : 0));
      return date;
    }
    return scheduledDate(day);
  };

  const start = days.length > 0 ? displayDate(days[0], 0) : null;
  const weekOf = start
    ? start.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : '';

  const todayIndex = days.findIndex((day, index) => {
    const date = displayDate(day, index);
    return !!date && isSameDay(date, today);
  });
  const todayDone = todayIndex >= 0 && days[todayIndex].done;
  // every completed day with an entry, newest first - showing only the latest
  // hid the days before it (two runs on the same weekday of different weeks
  // looked like a single one)
  const entryDays = days
    .filter(day => day.done && !!day.entry && !!day.dateOfCompletion)
    .sort((a, b) => new Date(b.dateOfCompletion as string).getTime() - new Date(a.dateOfCompletion as string).getTime());

  return (
    <View style={{ rowGap: SPACING['sp-16'] }}>
      <View
        style={{
          backgroundColor: COLORS.surface.contrast,
          borderRadius: SPACING['sp-16'],
          borderWidth: 1,
          borderColor: COLORS.border.secondary,
          padding: SPACING['sp-16'],
          rowGap: SPACING['sp-16'],
        }}
      >
        <Body1Bold>{(labels?.WEEK_OF || 'Week of') + ' ' + weekOf}</Body1Bold>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            rowGap: SPACING['sp-8'],
            columnGap: days.length > 7 ? SPACING['sp-8'] : undefined,
            // numberOfDays is admin-configurable: only a single week spreads
            justifyContent: days.length > 7 ? 'flex-start' : 'space-between',
          }}
        >
          {days.map((day, index) => {
            const date = displayDate(day, index);
            const weekday = date
              ? date.toLocaleDateString(undefined, { weekday: 'narrow' })
              : '';
            const isToday = index === todayIndex;
            return (
              <View key={index} style={{ alignItems: 'center', rowGap: SPACING['sp-8'] }}>
                <Caption1Reg color={COLORS.content.secondary}>{weekday}</Caption1Reg>
                <View
                  style={{
                    width: DAY_SIZE,
                    height: DAY_SIZE,
                    borderRadius: DAY_SIZE / 2,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: day.done
                      ? COLORS.state.success.primary
                      : isToday
                        ? COLORS.content.accent
                        : COLORS.surface.contrast,
                    borderWidth: day.done || isToday ? 0 : 1,
                    borderColor: COLORS.border.secondary,
                  }}
                >
                  {day.done ? (
                    <Caption1Bold color={COLORS.content['inverse-primary']}>
                      {convertFromUtf16('2713')}
                    </Caption1Bold>
                  ) : (
                    <Caption1Reg color={isToday ? COLORS.content['inverse-primary'] : COLORS.content.secondary}>
                      {date ? String(date.getDate()) : String(index + 1)}
                    </Caption1Reg>
                  )}
                </View>
              </View>
            );
          })}
        </View>
        {!todayDone && !!onPressCompleteToday && (
          <View style={{ alignSelf: 'flex-start' }}>
            <TouchableText
              onPress={onPressCompleteToday}
              label={(labels?.COMPLETE_TODAY || 'Complete today') + ' ✏️'}
            />
          </View>
        )}
      </View>

      {showEntry && entryDays.map((day, index) => (
        <View
          key={index}
          style={{
            backgroundColor: COLORS.surface.contrast,
            borderRadius: SPACING['sp-16'],
            borderWidth: 1,
            borderColor: COLORS.border.secondary,
            padding: SPACING['sp-16'],
            rowGap: SPACING['sp-8'],
          }}
        >
          <Body1Bold>
            {new Date(day.dateOfCompletion as string).toLocaleDateString(undefined, {
              weekday: 'long', month: 'long', day: 'numeric',
            })}
          </Body1Bold>
          <ActivityEntryBody entry={day.entry as string} />
        </View>
      ))}
    </View>
  );
};
